import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
} from "react-icons/fa";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";

// Helper to get full endpoint and token header
const buildConfig = (token) => ({
  baseURL: BASE_API_URL,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const endpoint = "/popular-routes";

const PopularRouteManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const [routes, setRoutes] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [formData, setFormData] = useState({
    from: "",
    to: "",
    distance: "",
    duration: "",
    sequence: 1,
    cabTypePrices: {},
  });

  // Get auth token
  const getAuthToken = () => {
    return user?.token;
  };

  // Fetch routes from API
  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      setIsLoading(true);
      const config = buildConfig(getAuthToken());
      const [routesResponse, cabTypesResponse] = await Promise.all([
        axios.get(`${endpoint}`, config),
        axios.get(`${BASE_API_URL}/cab`, config),
      ]);

      // Support both array returns and { data: [...] } shapes
      const routesData = Array.isArray(routesResponse.data)
        ? routesResponse.data
        : routesResponse.data?.data || [];

      const cabTypesData = cabTypesResponse.data?.data || cabTypesResponse.data || [];

      setRoutes(routesData);
      setCabTypes(cabTypesData);
    } catch (error) {
      console.error("Error fetching data:", error, error?.response?.data);
      const message = error?.response?.data?.message || error.message || "Failed to load data";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (route = null) => {
    if (route) {
      // Convert cabTypePrices array to object for easier form handling
      const cabTypePricesObj = {};
      if (route.cabTypePrices) {
        route.cabTypePrices.forEach((ctp) => {
          const cabTypeId = ctp.cabTypeId && (ctp.cabTypeId._id || ctp.cabTypeId) ? (ctp.cabTypeId._id || ctp.cabTypeId) : null;
          if (cabTypeId) cabTypePricesObj[cabTypeId] = ctp.price;
        });
      }

      setFormData({
        from: route.from || "",
        to: route.to || "",
        distance: route.distance || "",
        duration: route.duration || "",
        sequence: route.sequence || 1,
        cabTypePrices: cabTypePricesObj,
      });
      setCurrentRoute(route);
    } else {
      // Initialize cab type prices for new route
      const cabTypePricesObj = {};
      cabTypes.forEach((cabType) => {
        cabTypePricesObj[cabType._id] = 0;
      });

      setFormData({
        from: "",
        to: "",
        distance: "",
        duration: "",
        sequence: routes.length + 1,
        cabTypePrices: cabTypePricesObj,
      });
      setCurrentRoute(null);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentRoute(null);
    setFormData({
      from: "",
      to: "",
      distance: "",
      duration: "",
      sequence: 1,
      cabTypePrices: {},
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCabTypePriceChange = (cabTypeId, price) => {
    setFormData((prev) => ({
      ...prev,
      cabTypePrices: {
        ...prev.cabTypePrices,
        [cabTypeId]: parseInt(price) || 0,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.from.trim() ||
      !formData.to.trim() ||
      !formData.distance.trim() ||
      !formData.duration.trim() ||
      !formData.sequence
    ) {
      toast.error(
        "All fields (From, To, Distance, Duration, Sequence) are required!",
      );
      return;
    }

    // Validate that at least one cab type has a price set
    const hasValidPrices = Object.values(formData.cabTypePrices).some(
      (price) => price > 0,
    );
    if (!hasValidPrices) {
      toast.error("Please set prices for at least one cab type!");
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error("Not authenticated. Please log in as admin to perform this action.");
        return;
      }

      // Convert cab type prices object to array format for backend
      const cabTypePricesArray = Object.entries(formData.cabTypePrices).map(
        ([cabTypeId, price]) => ({
          cabTypeId,
          price: Number(price) || 0,
        }),
      );

      const routeData = {
        from: formData.from.trim(),
        to: formData.to.trim(),
        distance: formData.distance.trim(),
        duration: formData.duration.trim(),
        sequence: Number(formData.sequence) || 1,
        cabTypePrices: cabTypePricesArray,
      };

      const config = buildConfig(token);

      if (currentRoute) {
        // Update existing route
        const res = await axios.put(`${endpoint}/${currentRoute._id}`, routeData, config);
        toast.success(res.data?.message || "Route updated successfully!");
      } else {
        // Add new route
        const res = await axios.post(`${endpoint}`, routeData, config);
        toast.success(res.data?.message || "Route added successfully!");
      }

      handleCloseModal();
      fetchRoutes();
    } catch (error) {
      console.error("Error saving route:", error, error?.response?.data);
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message || JSON.stringify(error?.response?.data) || error.message;
      if (status === 401) {
        toast.error("Unauthorized: Please log in as admin.");
      } else if (status === 400) {
        toast.error(serverMessage);
      } else {
        toast.error(serverMessage || "Failed to save route");
      }
    }
  };

  const handleToggleStatus = async (routeId, currentStatus) => {
    try {
      const token = getAuthToken();
      if (!token) return toast.error("Not authenticated");
      const config = buildConfig(token);
      const res = await axios.put(`${endpoint}/${routeId}`, { isActive: !currentStatus }, config);
      toast.success(res.data?.message || "Route status updated successfully!");
      fetchRoutes();
    } catch (error) {
      console.error("Error updating route status:", error, error?.response?.data);
      const serverMessage = error?.response?.data?.message || error.message;
      toast.error(serverMessage || "Failed to update route status");
    }
  };

  const handleDelete = async (routeId, routeName) => {
    if (window.confirm(`Are you sure you want to delete "${routeName}"?`)) {
      try {
        const token = getAuthToken();
        if (!token) return toast.error("Not authenticated");
        const config = buildConfig(token);
        const res = await axios.delete(`${endpoint}/${routeId}`, config);
        toast.success(res.data?.message || "Route deleted successfully!");
        fetchRoutes();
      } catch (error) {
        console.error("Error deleting route:", error, error?.response?.data);
        const serverMessage = error?.response?.data?.message || error.message;
        toast.error(serverMessage || "Failed to delete route");
      }
    }
  };

  const filteredRoutes = routes
    .filter(
      (route) =>
        route.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        route.to.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Fixed Routes Management
          </h1>
          <p className="text-gray-600">
            Manage featured routes displayed on the homepage
          </p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaPlus /> Add Route
            </button>
          </div>
        </div>

        {/* Routes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading routes...</p>
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No routes found
            </div>
          ) : (
            filteredRoutes.map((route) => (
              <div
                key={route._id}
                className="bg-white rounded-lg shadow-sm border"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          #{route.sequence || 0}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {route.from} → {route.to}
                        </h3>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleToggleStatus(route._id, route.isActive)
                      }
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        route.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {route.isActive ? <FaToggleOn /> : <FaToggleOff />}
                      {route.isActive ? "Active" : "Inactive"}
                    </button>
                  </div>

                  <div className="space-y-1 text-sm text-gray-600 mb-4">
                    <p>
                      <span className="font-medium">Distance:</span>{" "}
                      {route.distance}
                    </p>
                    <p>
                      <span className="font-medium">Duration:</span>{" "}
                      {route.duration}
                    </p>
                  </div>

                  {/* Cab Type Prices */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Cab Type Prices:
                    </p>
                    <div className="space-y-2">
                      {route.cabTypePrices && route.cabTypePrices.length > 0 ? (
                        route.cabTypePrices.map((ctp) => {
                          const cabType = ctp.cabTypeId && (ctp.cabTypeId.name || (ctp.cabTypeId && ctp.cabTypeId._id)) ? (ctp.cabTypeId.name ? ctp.cabTypeId : { name: ctp.cabTypeId, _id: ctp.cabTypeId }) : { name: 'Unknown Cab Type', _id: ctp.cabTypeId };
                          const cabTypeId = cabType._id || cabType;
                          return (
                            <div
                              key={cabTypeId}
                              className="flex justify-between items-center bg-gray-50 p-2 rounded"
                            >
                              <span className="text-sm text-gray-800">
                                {cabType.name || "Unknown Cab Type"}
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                ₹{ctp.price}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="text-xs text-gray-500">
                          No prices set
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenModal(route)}
                      className="flex-1 flex items-center justify-center gap-1 bg-blue-100 text-blue-700 py-2 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(route._id, `${route.from} to ${route.to}`)
                      }
                      className="flex-1 flex items-center justify-center gap-1 bg-red-100 text-red-700 py-2 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  {currentRoute ? "Edit Route" : "Add New Route"}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From *
                      </label>
                      <input
                        type="text"
                        name="from"
                        value={formData.from}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Delhi"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To *
                      </label>
                      <input
                        type="text"
                        name="to"
                        value={formData.to}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Agra"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Distance *
                      </label>
                      <input
                        type="text"
                        name="distance"
                        value={formData.distance}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 250 km"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration *
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 4 hours"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sequence *
                      </label>
                      <input
                        type="number"
                        name="sequence"
                        value={formData.sequence}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="1"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  {/* Cab Type Pricing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Set Price for Each Cab Type *
                    </label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {cabTypes.map((cabType) => (
                        <div
                          key={cabType._id}
                          className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg"
                        >
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-800">
                              {cabType.name}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({cabType.acType} - {cabType.seatingCapacity}{" "}
                              seats)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">₹</span>
                            <input
                              type="number"
                              value={formData.cabTypePrices[cabType._id] || 0}
                              onChange={(e) =>
                                handleCabTypePriceChange(
                                  cabType._id,
                                  e.target.value,
                                )
                              }
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                      ))}
                      {cabTypes.length === 0 && (
                        <div className="text-center text-gray-500 py-4">
                          No cab types available
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {currentRoute ? "Update" : "Add"} Route
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopularRouteManagement;
